import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
} from '@mui/material';
import { teamsCallDeepLink, type TeamsChatSummary } from '@atlas/shared';
import { TeamsCallIcon, TeamsVideoIcon } from '@atlas/ui';

interface TeamsCallButtonsProps {
  chat: TeamsChatSummary;
}

/**
 * Start a Teams call for the open conversation.
 *
 * **The call happens in the user's Teams client, not here.** These are plain
 * deep links — no token, no Graph request, nothing server-side. ATLAS never
 * carries audio or video and never joins. Anything that would change that is a
 * different feature entirely (see the design doc's comparison with ACS).
 *
 * Renders nothing when the conversation has no `callTargets`, which the server
 * omits whenever it cannot address *every* participant — see that field's note
 * for why a partial call is worse than no button.
 *
 * Never gated on presence: calling somebody who is away and leaving voicemail
 * is legitimate, and a button that vanished based on a dot would read as broken
 * to anyone who did not know why.
 */
export function TeamsCallButtons({ chat }: TeamsCallButtonsProps) {
  const { t } = useTranslation();
  const [pendingVideo, setPendingVideo] = useState<boolean | null>(null);

  const targets = chat.callTargets;
  if (!targets || targets.length === 0) return null;

  // A 1:1 is unambiguous — one click, one person, and the presence dot beside
  // this already said whether that was reasonable. Ringing a group is an
  // outward-facing act with no undo, and the header gives no sense of who is in
  // it, so that one asks first.
  const needsConfirm = targets.length > 1;

  const audioLabel = t('teams.panel.callAudio', { name: chat.title });
  const videoLabel = t('teams.panel.callVideo', { name: chat.title });

  /**
   * Direct calls are ANCHORS, not `window.open`. A plain navigation is
   * something a popup blocker has no say over — which is exactly the failure
   * the pop-out button had to grow a fallback for. The confirmed path cannot be
   * an anchor (it has to wait for the dialog), but the confirm click is itself
   * a user gesture, so `window.open` is permitted there.
   */
  const anchorProps = (withVideo: boolean) =>
    needsConfirm
      ? { onClick: () => setPendingVideo(withVideo) }
      : {
          component: 'a' as const,
          href: teamsCallDeepLink(targets, withVideo),
          target: '_blank',
          rel: 'noopener noreferrer',
        };

  return (
    <>
      <Tooltip title={audioLabel}>
        <IconButton size="small" aria-label={audioLabel} {...anchorProps(false)}>
          <TeamsCallIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={videoLabel}>
        <IconButton size="small" aria-label={videoLabel} {...anchorProps(true)}>
          <TeamsVideoIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Dialog open={pendingVideo !== null} onClose={() => setPendingVideo(null)}>
        <DialogTitle>
          {t('teams.panel.callGroupConfirmTitle', { count: targets.length })}
        </DialogTitle>
        <DialogContent>
          {/* Names them rather than only counting: "call 6 people" gives the
              user no way to check it is the six they meant. */}
          <DialogContentText sx={{ wordBreak: 'break-word' }}>
            {t('teams.panel.callGroupConfirmBody', { people: targets.join(', ') })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingVideo(null)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={() => {
              window.open(
                teamsCallDeepLink(targets, pendingVideo === true),
                '_blank',
                'noopener,noreferrer',
              );
              setPendingVideo(null);
            }}
          >
            {t('teams.panel.callGroupConfirmCta')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
